import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Job, getFormData, formatFormType, FormDataResponse } from '../api/jobsService';

interface JobFormDataDialogProps {
  job: Job | null;
  open: boolean;
  onClose: () => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`form-data-tabpanel-${index}`}
      aria-labelledby={`form-data-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const JobFormDataDialog: React.FC<JobFormDataDialogProps> = ({ job, open, onClose }) => {
  const [formData, setFormData] = useState<FormDataResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      if (job && open) {
        setLoading(true);
        setError(null);
        try {
          const formSheetName = job.formSheetName || `BMD_${job.formType}`;
          console.log(`Fetching form data for ${formSheetName} / ${job.uuid}`);
          const data = await getFormData(formSheetName, job.uuid);
          setFormData(data);
        } catch (err: any) {
          // Provide a more detailed error message
          if (err.response) {
            // Server responded with an error status
            const errorDetail = err.response.data?.details || err.response.data?.error || err.message;
            setError(`API Error (${err.response.status}): ${errorDetail}`);
          } else if (err.request) {
            // Request was made but no response received
            setError('No response received from form data API. The server may be down.');
          } else {
            // Error setting up the request
            setError(`Error: ${err.message || 'Unknown error occurred'}`);
          }
          console.error('Error in form data dialog:', err);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [job, open]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  if (!job) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, height: '80vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">
            Form Details: {formatFormType(job.formType)} - {job.clientName}
          </Typography>
          <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <Divider />

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="form data tabs">
          <Tab label="Formatted View" id="form-data-tab-0" aria-controls="form-data-tabpanel-0" />
          <Tab label="Raw Data" id="form-data-tab-1" aria-controls="form-data-tabpanel-1" />
        </Tabs>
      </Box>

      <DialogContent dividers sx={{ p: 0 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3 }}>
            <Alert 
              severity="error" 
              sx={{ mb: 2 }}
            >
              {error}
            </Alert>
            <Typography variant="body2" color="text.secondary">
              This could indicate an issue with the form data API. Please check that the slack-form-handler service is running correctly.
            </Typography>
          </Box>
        ) : formData ? (
          <>
            {formData.note && (
              <Box sx={{ p: 2 }}>
                <Alert severity="info">
                  {formData.note}
                </Alert>
              </Box>
            )}
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                {formData.formData && typeof formData.formData === 'object' ? (
                  Object.entries(formData.formData).map(([key, value], index) => (
                    <Grid item xs={12} sm={6} md={4} key={index}>
                      <Paper elevation={1} sx={{ p: 2, height: '100%' }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom>
                          {key}
                        </Typography>
                        <Typography variant="body1">
                          {value || '-'}
                        </Typography>
                      </Paper>
                    </Grid>
                  ))
                ) : (
                  <Grid item xs={12}>
                    <Alert severity="info">
                      Formatted data not available in expected format
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </TabPanel>
            <TabPanel value={tabValue} index={1}>
              <TableContainer component={Paper} elevation={1}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Question</strong></TableCell>
                      <TableCell><strong>Value</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Array.isArray(formData.rawData) ? (
                      formData.rawData.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.question}</TableCell>
                          <TableCell>{item.value || '-'}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          <Typography variant="body2">Raw data not available in expected format</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </TabPanel>
          </>
        ) : (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1">No form data available</Typography>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default JobFormDataDialog;